import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchProducts, createProduct, updateProduct, deleteProduct, uploadProductImage, fetchCategories } from '../../../services/api';
import { Product, Category } from '../../../types';

interface FormState {
    name: string;
    description: string;
    customDescription: string;
    price: number;
    numberOfSessions: number;
    category: string;
    duration: string;
    minAge: number;
    maxAge: number;
    maxDepth: number;
    included: string;
    prerequisites: string;
}

const defaultFormState: FormState = {
    name: '',
    description: '',
    customDescription: '',
    price: 0,
    numberOfSessions: 1,
    category: '',
    duration: '',
    minAge: 8,
    maxAge: 99,
    maxDepth: 0,
    included: '',
    prerequisites: ''
};

const AdminProducts: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [formState, setFormState] = useState<FormState>(defaultFormState);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const [productsData, categoriesData] = await Promise.all([
                fetchProducts(),
                fetchCategories()
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
        } catch (error) {
            console.error('Erreur chargement produits:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingProduct(null);
        setSelectedImage(null);
        setFormState(defaultFormState);
        setSaveError(null);
    };

    const handleOpenDialog = (product?: Product) => {
        setSaveError(null);
        if (product) {
            setEditingProduct(product);
            setFormState({
                name: product.name || '',
                description: product.description || '',
                customDescription: product.customDescription || '',
                price: product.price || 0,
                numberOfSessions: product.numberOfSessions || 1,
                category: product.category?._id || '',
                duration: product.details?.duration || '',
                minAge: product.details?.minAge || 8,
                maxAge: product.details?.maxAge || 99,
                maxDepth: product.details?.maxDepth || 0,
                included: product.details?.included?.join('\n') || '',
                prerequisites: product.details?.prerequisites?.join('\n') || ''
            });
        } else {
            setEditingProduct(null);
            setFormState(defaultFormState);
        }
        setSelectedImage(null);
        setOpenDialog(true);
    };

    const handleFormChange = (field: keyof FormState, value: string | number) => {
        setFormState(prev => ({ ...prev, [field]: value }));
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedImage(event.target.files[0]);
        } else {
            setSelectedImage(null);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSaveError(null);

        const slug = formState.name.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();

        const productData = {
            name: formState.name,
            slug: slug,
            description: formState.description,
            customDescription: formState.customDescription,
            price: Number(formState.price),
            category: formState.category,
            numberOfSessions: Number(formState.numberOfSessions),
            details: {
                duration: formState.duration,
                minAge: Number(formState.minAge),
                maxAge: Number(formState.maxAge),
                maxDepth: Number(formState.maxDepth),
                included: formState.included.split('\n').filter(Boolean),
                prerequisites: formState.prerequisites.split('\n').filter(Boolean)
            }
        };

        try {
            if (editingProduct) {
                await updateProduct(editingProduct._id, productData);
                if (selectedImage) {
                    await uploadProductImage(editingProduct._id, selectedImage);
                }
            } else {
                const newProduct = await createProduct(productData);
                if (selectedImage) {
                    await uploadProductImage(newProduct._id, selectedImage);
                }
            }
            await loadProducts();
            handleCloseDialog();
        } catch (error) {
            setSaveError('Erreur lors de la sauvegarde: ' + ((error as Error).message || 'Erreur inconnue'));
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
            try {
                await deleteProduct(id);
                await loadProducts();
            } catch (error) {
                console.error('Erreur suppression:', error);
            }
        }
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    Gérer les produits
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Ajouter un produit
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell>Catégorie</TableCell>
                            <TableCell>Prix</TableCell>
                            <TableCell>Sessions</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((product) => (
                            <TableRow key={product._id}>
                                <TableCell>{product.name}</TableCell>
                                <TableCell>
                                    {categories.find(c => c._id === product.category?._id)?.name || 'Non catégorisé'}
                                </TableCell>
                                <TableCell>{product.price}€</TableCell>
                                <TableCell>
                                    {product.numberOfSessions && product.numberOfSessions > 1 
                                        ? <strong style={{ color: '#1976d2' }}>{product.numberOfSessions}</strong> 
                                        : '1'}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenDialog(product)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(product._id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {saveError && (
                            <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
                        )}
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Image du produit
                                </Typography>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ marginBottom: 16 }}
                                />

                                {editingProduct?.imageUrl && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Image actuelle :
                                        </Typography>
                                        <img
                                            src={editingProduct.imageUrl}
                                            alt="Image actuelle"
                                            style={{
                                                maxWidth: 200,
                                                maxHeight: 200,
                                                objectFit: 'cover',
                                                border: '1px solid #ccc',
                                                borderRadius: 4
                                            }}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}

                                {selectedImage && (
                                    <div>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Nouvelle image sélectionnée :
                                        </Typography>
                                        <img
                                            src={URL.createObjectURL(selectedImage)}
                                            alt="Aperçu"
                                            style={{
                                                maxWidth: 200,
                                                maxHeight: 200,
                                                objectFit: 'cover',
                                                border: '1px solid #4caf50',
                                                borderRadius: 4
                                            }}
                                        />
                                    </div>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nom"
                                    value={formState.name}
                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={4}
                                    value={formState.description}
                                    onChange={(e) => handleFormChange('description', e.target.value)}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description personnalisée du produit"
                                    multiline
                                    rows={4}
                                    value={formState.customDescription}
                                    onChange={(e) => handleFormChange('customDescription', e.target.value)}
                                    helperText="Cette description apparaîtra dans un pavé spécial sur la fiche produit"
                                    placeholder="Décrivez les spécificités de ce produit..."
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Prix (€)"
                                    type="number"
                                    value={formState.price}
                                    onChange={(e) => handleFormChange('price', Number(e.target.value))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Nombre de sessions"
                                    type="number"
                                    value={formState.numberOfSessions}
                                    onChange={(e) => handleFormChange('numberOfSessions', Number(e.target.value))}
                                    inputProps={{ min: 1 }}
                                    helperText="Ex: 10 pour carte 10 plongées"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Catégorie</InputLabel>
                                    <Select
                                        value={formState.category}
                                        onChange={(e) => handleFormChange('category', e.target.value)}
                                        label="Catégorie"
                                        required
                                    >
                                        {categories.map((category) => (
                                            <MenuItem key={category._id} value={category._id}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Durée"
                                    value={formState.duration}
                                    onChange={(e) => handleFormChange('duration', e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Âge minimum"
                                    type="number"
                                    value={formState.minAge}
                                    onChange={(e) => handleFormChange('minAge', Number(e.target.value))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Âge maximum"
                                    type="number"
                                    value={formState.maxAge}
                                    onChange={(e) => handleFormChange('maxAge', Number(e.target.value))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Profondeur maximale"
                                    type="number"
                                    value={formState.maxDepth}
                                    onChange={(e) => handleFormChange('maxDepth', Number(e.target.value))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Inclus"
                                    multiline
                                    rows={3}
                                    value={formState.included}
                                    onChange={(e) => handleFormChange('included', e.target.value)}
                                    helperText="Un élément par ligne"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Prérequis"
                                    multiline
                                    rows={3}
                                    value={formState.prerequisites}
                                    onChange={(e) => handleFormChange('prerequisites', e.target.value)}
                                    helperText="Un élément par ligne"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Annuler</Button>
                        <Button type="submit" variant="contained" color="primary">
                            {editingProduct ? 'Modifier' : 'Ajouter'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
};

export default AdminProducts;
